"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import Link from "next/link";
import { ChevronDown, X } from "lucide-react";
import type { VolumeChapterMetadata } from "@/lib/reader/volume-chapter-groups";
import { VolumeContentsControls } from "@/components/reader/volume-contents-controls";
import { useVolumeChapterGroups } from "@/components/reader/use-volume-chapter-groups";
import { cn } from "@/lib/utils";
export interface ReaderChapterMeta extends VolumeChapterMetadata {
  title: string;
}
interface ReaderTableOfContentsProps {
  chapters: ReaderChapterMeta[];
  currentChapterId: string;
  storySlug: string;
  isPreview: boolean;
  isNavigating: boolean;
  onClose: () => void;
  onNavigate: (chapterId: string) => void;
}
interface ReaderChapterRowsProps {
  chapters: ReaderChapterMeta[];
  currentChapterId: string;
  storySlug: string;
  isPreview: boolean;
  isNavigating: boolean;
  currentChapterRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onNavigate: (chapterId: string) => void;
}
function ReaderChapterRows({
  chapters,
  currentChapterId,
  storySlug,
  isPreview,
  isNavigating,
  currentChapterRef,
  onClose,
  onNavigate
}: ReaderChapterRowsProps) {
  return chapters.map(chapter => {
    const isCurrent = chapter.id === currentChapterId;
    const commonClasses = cn("toc-drawer__item", isCurrent && "toc-drawer__item--active");
    const handleClick = (event: React.MouseEvent) => {
      event.preventDefault();
      if (isNavigating) return;
      onClose();
      onNavigate(chapter.id);
    };
    const content = <>
                <span className="toc-drawer__title">{chapter.title}</span>
                {isCurrent && <span className="toc-drawer__badge">Reading</span>}
            </>;
    return isPreview ? <button key={chapter.id} ref={isCurrent ? node => {
      currentChapterRef.current = node;
    } : undefined} onClick={handleClick} className={cn(commonClasses, "w-full text-left")}>
                {content}
            </button> : <Link key={chapter.id} ref={isCurrent ? node => {
      currentChapterRef.current = node;
    } : undefined} href={`/story/${storySlug}/read?chapter=${chapter.id}`} onClick={handleClick} className={commonClasses}>
                {content}
            </Link>;
  });
}
export function ReaderTableOfContents(t0) {
  const $ = _c(42);
  const {
    chapters,
    currentChapterId,
    storySlug,
    isPreview,
    isNavigating,
    onClose,
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== chapters || $[1] !== currentChapterId) {
    t1 = chapters.find(chapter => chapter.id === currentChapterId)?.volumeId;
    $[0] = chapters;
    $[1] = currentChapterId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentVolumeId = t1;
  const currentChapterRef = useRef(null);
  const {
    areAllExpanded,
    direction,
    expandedVolumeIds,
    groups,
    hasMultipleVolumes,
    toggleAllVolumes,
    toggleDirection,
    toggleVolume
  } = useVolumeChapterGroups(chapters, currentVolumeId);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const animationFrameId = requestAnimationFrame(() => currentChapterRef.current?.scrollIntoView?.({
        block: "center"
      }));
      return () => cancelAnimationFrame(animationFrameId);
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h2 className="font-heading text-lg font-bold">Table of Contents</h2>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = chapters.length !== 1 ? "s" : "";
  const t6 = hasMultipleVolumes && ` · ${groups.length} volumes`;
  let t7;
  if ($[6] !== chapters.length || $[7] !== t5 || $[8] !== t6) {
    t7 = <div>{t4}<p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{chapters.length} chapter{t5}{t6}</p></div>;
    $[6] = chapters.length;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <X className="size-5" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== onClose) {
    t9 = <button onClick={onClose} aria-label="Close table of contents" className="rounded-lg p-1.5 text-slate-500 transition-colors hover:text-primary dark:text-slate-400">{t8}</button>;
    $[11] = onClose;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t7 || $[14] !== t9) {
    t10 = <div className="toc-drawer__header">{t7}{t9}</div>;
    $[13] = t7;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== areAllExpanded || $[17] !== direction || $[18] !== hasMultipleVolumes || $[19] !== toggleAllVolumes || $[20] !== toggleDirection) {
    t11 = <VolumeContentsControls direction={direction} areAllExpanded={areAllExpanded} onToggleDirection={toggleDirection} onToggleAll={toggleAllVolumes} showVolumeToggle={hasMultipleVolumes} className="px-5 pb-3 pt-2" />;
    $[16] = areAllExpanded;
    $[17] = direction;
    $[18] = hasMultipleVolumes;
    $[19] = toggleAllVolumes;
    $[20] = toggleDirection;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== currentChapterId || $[23] !== expandedVolumeIds || $[24] !== groups || $[25] !== hasMultipleVolumes || $[26] !== isNavigating || $[27] !== isPreview || $[28] !== onClose || $[29] !== onNavigate || $[30] !== storySlug || $[31] !== toggleVolume) {
    t12 = hasMultipleVolumes ? groups.map(group => {
      const isExpanded = expandedVolumeIds.has(group.id);
      return <section key={group.id} className="toc-drawer__volume"><h3><button type="button" onClick={() => toggleVolume(group.id)} aria-expanded={isExpanded} aria-controls={`reader-volume-panel-${group.id}`} className="toc-drawer__volume-trigger"><ChevronDown className={cn("size-4 transition-transform", !isExpanded && "-rotate-90")} /><span className="min-w-0 flex-1 truncate">{group.title}</span></button></h3>{isExpanded && <div id={`reader-volume-panel-${group.id}`}><ReaderChapterRows chapters={group.chapters} currentChapterId={currentChapterId} storySlug={storySlug} isPreview={isPreview} isNavigating={isNavigating} currentChapterRef={currentChapterRef} onClose={onClose} onNavigate={onNavigate} /></div>}</section>;
    }) : <ReaderChapterRows chapters={groups[0]?.chapters ?? []} currentChapterId={currentChapterId} storySlug={storySlug} isPreview={isPreview} isNavigating={isNavigating} currentChapterRef={currentChapterRef} onClose={onClose} onNavigate={onNavigate} />;
    $[22] = currentChapterId;
    $[23] = expandedVolumeIds;
    $[24] = groups;
    $[25] = hasMultipleVolumes;
    $[26] = isNavigating;
    $[27] = isPreview;
    $[28] = onClose;
    $[29] = onNavigate;
    $[30] = storySlug;
    $[31] = toggleVolume;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12) {
    t13 = <div className="toc-drawer__list">{t12}</div>;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t13) {
    t14 = <div className="toc-drawer" onClick={_temp}>{t10}{t11}{t13}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== onClose || $[40] !== t14) {
    t15 = <div className="toc-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Table of Contents">{t14}</div>;
    $[39] = onClose;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
}
function _temp(event) {
  return event.stopPropagation();
}